import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

/**
 * EL IDENTIFICADOR DE MARTY.
 *
 * Fuente de verdad: «Brandbook Marty» v1.0, laminas 09 a 14. Tres decisiones lo
 * definen, y las tres estan en el codigo de abajo:
 *
 *   El punto final.   Marty no propone: afirma. Es el punto de «todo cuadra»,
 *                     la cuenta cerrada. Siempre en rojo margen, siempre al 100%
 *                     —nunca translucido, nunca en degrade—.
 *   La linea de margen. Heredada del papel rayado del contador. Corre en vertical
 *                     a la izquierda de la M, separada 22% del cuerpo tipografico,
 *                     con un grosor del 3% del cuerpo, y atraviesa el soporte de
 *                     arriba a abajo. NUNCA toca la letra.
 *   El rayado sutil.  No va en el identificador: es textura de soporte. Vive en
 *                     la clase `.rayado` y se omite en tamanos pequenos.
 *
 * REDUCCION MINIMA, que es la razon de que haya tres variantes y no una:
 *   - `completo`  logotipo con linea. Minimo 80px de ancho.
 *   - `compacto`  solo «Marty.», sin linea. Minimo 48px.
 *   - el icono    `<Logomark>`. Minimo 16px.
 *
 * Por debajo de cada umbral la pieza correspondiente deja de leerse, asi que en
 * vez de encogerla se cambia de variante. Quien monte la marca en un sitio
 * estrecho elige `compacto`; no hay una version chica del completo.
 */

interface LogomarkProps {
  className?: string
}

/**
 * El icono: el sistema condensado en un cuadrado. Linea de margen a la
 * izquierda, la M en el trazo de Onest SemiBold, punto rojo. Sin rayado.
 *
 * La M va dibujada como trazo y no como `<text>` a proposito: este mismo dibujo
 * se usa en el favicon y en avatares, contextos donde la webfont puede no estar
 * cargada y un `font-family: Onest` caeria a la letra del sistema.
 *
 * El radio es 20% del lado, como fija el brandbook.
 */
export function Logomark({ className }: LogomarkProps) {
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden="true"
      className={cn("h-5 w-5", className)}
    >
      <rect width="64" height="64" rx="12.8" className="fill-brand" />
      <rect x="13" y="0" width="2" height="64" className="fill-margen" />
      <path
        d="M22 44V22l12 14 12-14v22"
        fill="none"
        strokeWidth="5.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="stroke-brand-foreground"
      />
      <circle cx="54.6" cy="41.4" r="3.3" className="fill-margen" />
    </svg>
  )
}

interface LogotypeProps {
  className?: string
  /**
   * `completo` lleva la linea de margen; `compacto` no. Ver reduccion minima.
   *
   * `sobreLinea` es el tercero y el que faltaba: el logotipo SIN su propia linea,
   * porque la linea la dibuja el SOPORTE y atraviesa la pieza de arriba abajo.
   * Ver `LineaDeMargen`.
   */
  variante?: "completo" | "compacto" | "sobreLinea"
  /**
   * El cuerpo tipografico. Todo lo demas del identificador —el grosor de la
   * linea, su separacion, el punto— esta en `em`, asi que cambiar esto reescala
   * la construccion entera sin deformarla.
   */
  tamano?: keyof typeof CUERPOS
}

/**
 * Los cuerpos disponibles, y el minimo de cada contexto.
 *
 * El brandbook fija reduccion minima por ancho (80px el completo, 48px el
 * compacto). Aqui se expresa en cuerpo tipografico, que es lo que se controla en
 * CSS: `sm` es el piso, y por debajo se cambia a `<Logomark>`.
 */
const CUERPOS = {
  sm: "text-[1.0625rem]",   // el que habia: chrome estrecho
  md: "text-[1.375rem]",    // riel de navegacion
  lg: "text-[2rem]",        // cabecera de una pieza
  xl: "text-[3rem]",        // pieza grande
  /*
    LA PORTADA. Cesar, del login: «el logo de Marty deberia de ser mas grande».
    En el login el identificador no acompana a nada: ES la pieza, y es lo primero
    y casi lo unico que hay que ver antes de pulsar un boton. A 3rem competia con
    el titular que va debajo; a 4rem lo encabeza.
  */
  "2xl": "text-[4rem]",     // portada: login
} as const

interface LineaDeMargenProps {
  className?: string
  /**
   * Distancia desde el borde izquierdo del soporte. En `rem` para que coincida
   * con el padding de la pieza: la linea tiene que quedar a 22% del cuerpo a la
   * izquierda de la M, y eso se consigue alineando el texto, no la linea.
   */
  left?: string
  grosor?: string
}

/**
 * LA LINEA DE MARGEN DEL SOPORTE.
 *
 * ESTO ES LO QUE FALTABA, y no es un detalle de estilo. El brandbook (lamina 09)
 * dice de la linea: «corre en vertical a la izquierda de la M, separada 22% del
 * cuerpo tipografico, y **atraviesa el soporte de arriba a abajo**».
 *
 * Hasta ahora la linea vivia dentro de `<Logotype>` y medía lo que medía el
 * logotipo: veinte pixeles de alto en una esquina. Eso la convierte en un
 * adorno del logo, cuando es el eje de la pieza — el renglon del libro rayado
 * del contador, que empieza arriba y termina abajo.
 *
 * Asi que la linea es del SOPORTE: la dibuja la pantalla, y el logotipo se
 * coloca encima con la variante `sobreLinea` para no duplicarla.
 *
 * UNA SOLA POR PIEZA. El rojo «senala, no tapiza: nunca mas del 5% de una
 * superficie». Una pantalla con la linea de margen ya gastó su acento rojo: ahi
 * el elemento activo de un menu NO puede ser otro filete rojo, y por eso el riel
 * marca lo activo en folio (lamina 20) en vez de con un lomo rojo.
 */
export function LineaDeMargen({
  className,
  left = "0",
  grosor = "2px",
}: LineaDeMargenProps) {
  return (
    <span
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-y-0 bg-margen", className)}
      style={{ left, width: grosor }}
    />
  )
}

/**
 * El logotipo: la palabra «Marty» en Onest SemiBold con interletrado -3% (la
 * clase `.titular`), seguida del punto rojo.
 *
 * NO lleva bajada ni descriptor. El area de seguridad del brandbook reserva un
 * margen de 2X —dos diametros del punto— alrededor del identificador, y una
 * linea de texto pegada debajo lo invade. La version anterior de este componente
 * apilaba un «Contabilidad + CFO» ahi mismo.
 *
 * Color: version PRINCIPAL en tema claro (verde libro sobre folio) y NEGATIVA en
 * oscuro (folio sobre verde libro). El punto conserva el rojo en las dos, que es
 * justo lo que las diferencia de la version monocroma.
 */
export function Logotype({
  className,
  variante = "completo",
  tamano = "sm",
}: LogotypeProps) {
  return (
    <div
      className={cn(
        "relative flex h-full items-center leading-none",
        CUERPOS[tamano],
        "text-brand dark:text-foreground",
        className,
      )}
      /* 22% del cuerpo de separacion entre la linea y la M. En em, para que la
         proporcion se conserve a cualquier tamano. */
      /* La separacion de 22% del cuerpo entre la linea y la M. Vale para las dos
         variantes que llevan linea: la propia (`completo`) y la del soporte
         (`sobreLinea`), porque en las dos la M tiene que arrancar a 22%. */
      style={variante === "compacto" ? undefined : { paddingLeft: "0.22em" }}
    >
      {variante === "completo" ? (
        <span
          aria-hidden="true"
          className="absolute left-0 top-0 h-full bg-margen"
          /* 3% del cuerpo de grosor, con un piso de 1px: por debajo de un pixel
             el navegador la difumina y la linea se lee como una sombra. */
          style={{ width: "max(1px, 0.03em)" }}
        />
      ) : null}
      {/* `translate-y` optico: el punto de Onest se apoya en la linea de base y
          el bloque de texto centrado geometricamente queda un pelo alto. */}
      <span className="titular translate-y-[-0.02em]">
        {t("Marty")}<span className="text-margen">.</span>
      </span>
      <span className="sr-only">{t("Marty — tus finanzas, resueltas")}</span>
    </div>
  )
}
